import { useMemo } from 'react'
import { useCollectionStatus } from '@/hooks/collections/useCollectionStatus'
import { buildBaseCardCounts, buildDeckCardMap, computeHasUnsavedChanges } from '@/hooks/decks/deckBuilderCards'
import { useDeckBuylist } from '@/hooks/decks/useDeckBuylist'
import { useDeckDraft } from '@/hooks/decks/useDeckDraft'
import { useDeck, useUpdateDeck, useDeleteDeck } from '@/hooks/decks/useDecks'
import { useDeckSave } from '@/hooks/decks/useDeckSave'
import { useDeckStats } from '@/hooks/decks/useDeckStats'
import { useCard } from '@/hooks/products/useCards'

interface UseDeckBuilderOptions {
  deckId?: number
  isEditing: boolean
}

interface TogglePublicArgs {
  deckId?: number
  isEditing: boolean
  isPublic: boolean
  setIsPublic: (value: boolean) => void
  updateDeck: ReturnType<typeof useUpdateDeck>
}

/** Toggle public (saves immediately for existing decks). */
function useTogglePublic({ deckId, isEditing, isPublic, setIsPublic, updateDeck }: TogglePublicArgs) {
  return async (newValue: boolean) => {
    const previous = isPublic
    setIsPublic(newValue) // optimistic
    if (isEditing && deckId) {
      try {
        await updateDeck.mutateAsync({ id: deckId, data: { isPublic: newValue } })
      } catch {
        // Revert the optimistic toggle so the checkbox reflects the un-saved
        // server state. updateDeck carries meta.inlineErrorStatuses:[422], so a
        // 422 here is globally suppressed — without this revert a rejected
        // toggle would be a silent no-op leaving the UI out of sync. Non-422
        // failures still surface via the global MutationCache toast; swallowing
        // here just prevents an unhandled rejection (the onChange isn't awaited).
        setIsPublic(previous)
      }
    }
  }
}

export function useDeckBuilder({ deckId, isEditing }: UseDeckBuilderOptions) {
  const { data: deckData, isLoading: deckLoading } = useDeck(deckId)
  const draft = useDeckDraft(deckData)
  const { localCards, selectedLeaderId, isPublic, setIsPublic } = draft

  // Leader resolution
  const { data: selectedLeaderData } = useCard(selectedLeaderId ?? 0)
  const selectedLeader = !isEditing && selectedLeaderData ? selectedLeaderData : null
  const leader = deckData?.deck.leader ?? selectedLeader

  const updateDeck = useUpdateDeck()
  const deleteDeck = useDeleteDeck()

  const hasUnsavedChanges = useMemo(
    () =>
      computeHasUnsavedChanges(
        isEditing,
        {
          name: draft.deckName,
          description: draft.deckDescription,
          leaderId: selectedLeaderId,
          cards: localCards,
        },
        deckData?.deck,
        draft.serverCardsSnapshot,
      ),
    [
      isEditing,
      draft.deckName,
      draft.deckDescription,
      selectedLeaderId,
      localCards,
      draft.serverCardsSnapshot,
      deckData,
    ],
  )
  const deckCardMap = useMemo(() => buildDeckCardMap(localCards), [localCards])
  // Total copies per base card (cardImageId) for the 4-copy rule
  const baseCardCounts = useMemo(() => buildBaseCardCounts(localCards), [localCards])

  const localStats = useDeckStats(localCards, leader)
  const collectionStatus = useCollectionStatus(localCards, leader)
  const { addingToBuylist, handleAddToBuylist } = useDeckBuylist(collectionStatus)

  const { isSaving, handleSave } = useDeckSave({
    deckId,
    isEditing,
    name: draft.deckName,
    description: draft.deckDescription,
    leaderId: selectedLeaderId,
    isPublic,
    localCards,
    serverCardsSnapshot: draft.serverCardsSnapshot,
    setServerCardsSnapshot: draft.setServerCardsSnapshot,
    setErrors: draft.setErrors,
    updateDeck,
  })

  const handleTogglePublic = useTogglePublic({ deckId, isEditing, isPublic, setIsPublic, updateDeck })

  return {
    deckLoading,
    deckData,
    deckName: draft.deckName,
    setDeckName: draft.setDeckName,
    deckDescription: draft.deckDescription,
    setDeckDescription: draft.setDeckDescription,
    selectedLeaderId,
    setSelectedLeaderId: draft.setSelectedLeaderId,
    isPublic,
    localCards,
    isSaving,
    errors: draft.errors,
    hasUnsavedChanges,
    leader,
    deckCardMap,
    baseCardCounts,
    localStats,
    collectionStatus,
    handleAddCard: draft.handleAddCard,
    handleRemoveCard: draft.handleRemoveCard,
    handleSave,
    handleTogglePublic,
    deleteDeck,
    addingToBuylist,
    handleAddToBuylist,
    isUpdating: updateDeck.isPending,
  }
}
